// packages/common/sentry/src/browser.ts
import { init as naturalInit, addBreadcrumb as naturalAddBreadcrumb, captureException as naturalCaptureException, Replay, setTag } from "@sentry/browser";
import { CaptureConsole, HttpClient } from "@sentry/integrations";
import { BrowserTracing } from "@sentry/tracing";
import { log as log2 } from "@dxos/log";

// packages/common/sentry/src/tracing.ts
import { setUser, getCurrentHub } from "@sentry/browser";
import { runInContext, scheduleTask, Trigger } from "@dxos/async";
import { Context } from "@dxos/context";
import { getContextFromEntry, log, LogLevel } from "@dxos/log";
var TX;
var SPAN_MAP = /* @__PURE__ */ new Map();
var SENTRY_INITIALIZED = new Trigger();
var ctx = new Context({
  onError: (err) => log.warn("Unhandled error in Sentry context", err, {
    file: "tracing.ts",
    line: 15,
    scope: void 0,
    callSite: (f, a) => f(...a)
  })
});
var configureTracing = () => {
  runInContext(ctx, () => {
    TX = getCurrentHub().startTransaction({
      name: "DXOS Core Tracing",
      op: "dxos"
    });
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", finish);
    }
    if (typeof process !== "undefined") {
      process.on("exit", finish);
    }
    SENTRY_INITIALIZED.wake();
  });
};
var finish = () => {
  for (const span of SPAN_MAP.values()) {
    span.finish();
  }
  TX.finish();
};
var SENTRY_PROCESSOR = (config, entry) => {
  if (entry.level !== LogLevel.TRACE) {
    return;
  }
  scheduleTask(ctx, async () => {
    var _a;
    await SENTRY_INITIALIZED.wait();
    const context = getContextFromEntry(entry);
    if (entry.message === "dxos.halo.identity" && (context == null ? void 0 : context.identityKey)) {
      setUser({
        id: context.identityKey,
        username: context.displayName
      });
    }
    if (context == null ? void 0 : context.span) {
      switch (context.span.command) {
        case "begin": {
          const id = context.span.id;
          if (!id || SPAN_MAP.has(id)) {
            log.warn("Cannot begin span", id, {
              file: "tracing.ts",
              line: 63,
              scope: void 0,
              callSite: (f, a) => f(...a)
            });
            return;
          }
          let parentSpan = TX;
          if (context.span.parent) {
            parentSpan = (_a = SPAN_MAP.get(context.span.parent)) != null ? _a : TX;
          }
          let logContext;
          try {
            logContext = JSON.stringify({
              ...context,
              ...entry
            });
          } catch (err) {
            logContext = JSON.stringify(context);
          }
          const span = parentSpan.startChild({
            op: entry.message,
            data: {
              ...context.span.data,
              "@dxos/log": logContext
            }
          });
          SPAN_MAP.set(context.span.id, span);
          break;
        }
        case "end": {
          const span1 = SPAN_MAP.get(context.span.id);
          if (span1) {
            span1.setStatus(getSpanStatus(context.span.status));
            context.span.data && Object.entries(context.span.data).forEach(([key, value]) => span1.setData(key, value));
            span1.finish();
            SPAN_MAP.delete(context.span.id);
          } else {
            log.warn("Cannot end span", context.span.id, {
              file: "tracing.ts",
              line: 98,
              scope: void 0,
              callSite: (f, a) => f(...a)
            });
          }
          break;
        }
        case "update": {
          const span2 = SPAN_MAP.get(context.span.id);
          if (span2) {
            context.span.data && Object.entries(context.span.data).forEach(([key, value]) => span2.setData(key, value));
          } else {
            log.warn("Cannot update span", context.span.id, {
              file: "tracing.ts",
              line: 108,
              scope: void 0,
              callSite: (f, a) => f(...a)
            });
          }
          break;
        }
        default: {
          log.warn("Unknown span command", context.span.command, {
            file: "tracing.ts",
            line: 114,
            scope: void 0,
            callSite: (f, a) => f(...a)
          });
        }
      }
    }
  });
};
log.runtimeConfig.processors.push(SENTRY_PROCESSOR);
var getSpanStatus = (status) => {
  switch (status) {
    case "ok":
      return "ok";
    case "error":
      return "not_found";
  }
  return status != null ? status : "ok";
};

// packages/common/sentry/src/browser.ts
var init = (options) => {
  var _a, _b;
  try {
    log2("sentry init", options, {
      file: "browser.ts",
      line: 29,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    naturalInit({
      enabled: (_a = options.enable) != null ? _a : true,
      dsn: options.destination,
      release: options.release,
      environment: options.environment,
      integrations: [
        new CaptureConsole({
          levels: [
            "error",
            "warn"
          ]
        }),
        new HttpClient({
          failedRequestStatusCodes: [
            [
              400,
              599
            ]
          ]
        }),
        ...options.tracing ? [
          new BrowserTracing()
        ] : [],
        ...options.replay ? [
          new Replay({
            blockAllMedia: true,
            maskAllText: true
          })
        ] : []
      ],
      replaysSessionSampleRate: options.replaySampleRate,
      replaysOnErrorSampleRate: options.replaySampleRateOnError,
      tracesSampleRate: options.sampleRate,
      transport: options.transport,
      beforeSend: (event) => {
        var _a2;
        (_a2 = options.onError) == null ? void 0 : _a2.call(options, event);
        return event;
      }
    });
    Object.entries((_b = options.properties) != null ? _b : {}).forEach(([key, value]) => {
      setTag(key, value);
    });
  } catch (err) {
    log2.catch("Failed to initialize sentry", err, {
      file: "browser.ts",
      line: 55,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
  }
};
var addBreadcrumb = (breadcrumb) => {
  try {
    naturalAddBreadcrumb(breadcrumb);
    log2("add breadcrumb", breadcrumb, {
      file: "browser.ts",
      line: 71,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
  } catch (err) {
    log2.catch("Failed to add breadcrumb", err, {
      file: "browser.ts",
      line: 73,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
  }
};
var captureException = (exception, captureContext) => {
  try {
    const eventId = naturalCaptureException(exception, captureContext);
    log2("capture exception", {
      exception,
      eventId,
      ...captureContext
    }, {
      file: "browser.ts",
      line: 87,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    return eventId;
  } catch (err) {
    log2.catch("Failed to capture exception", err, {
      file: "browser.ts",
      line: 90,
      scope: void 0,
      callSite: (f, a) => f(...a)
    });
    return "unknown";
  }
};
export {
  SENTRY_PROCESSOR,
  addBreadcrumb,
  captureException,
  configureTracing,
  finish,
  init
};
//# sourceMappingURL=index.mjs.map
